import { useEffect, useMemo, useRef, useState } from 'react';
import { apiEndpoints } from '../../utils/apiEndpoint';
import { useNavigate } from 'react-router-dom';
import http from '../../services/http';
import { AxiosError } from 'axios';

type SearchObject = {
    api_name?: string;
    label?: string;
};

type QuickAction = {
    id: string;
    label: string;
    url?: string;
    method?: string;
    confirm?: string | null;
    nested?: boolean;
};

type SearchResult = {
    can_update?: boolean;
    entity?: string;
    entity_id?: string;
    payload?: {
        casenumber?: number;
        name?: string;
        crm_id?: string;
        id?: string;
    };
    search_text?: string;
};

type NavItem = { kind: 'action'; action: QuickAction } | { kind: 'result'; result: SearchResult; to: string | null };

function GlobalSearch() {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [searchObjects, setSearchObjects] = useState<SearchObject[]>([]);
    const [selected, setSelected] = useState('all');
    const [isOptionsLoading, setIsOptionsLoading] = useState(false);

    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    const [actions, setActions] = useState<QuickAction[]>([]);
    const [stepQuery, setStepQuery] = useState('');
    const [actionTrail, setActionTrail] = useState<
        {
            id: string;
            label: string;
            parentStepQuery: string;
            parentOptions: QuickAction[];
        }[]
    >([]);
    const skipNextFetchRef = useRef(false);
    const [actionOptions, setActionOptions] = useState<QuickAction[]>([]);
    const [isOptionsFetching, setIsOptionsFetching] = useState(false);
    const [csrf, setCsrf] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);

    const navigate = useNavigate();

    const entityLabels = useMemo(() => {
        return searchObjects.reduce<Record<string, string>>((labels, obj) => {
            if (obj.api_name) {
                labels[obj.api_name] = obj.label || obj.api_name;
            }
            return labels;
        }, {});
    }, [searchObjects]);

    const navItems = useMemo<NavItem[]>(() => {
        if (actionTrail.length > 0) {
            return actionOptions.map((action) => ({ kind: 'action' as const, action }));
        }
        return [
            ...actions.map((action) => ({ kind: 'action' as const, action })),
            ...results.map((result) => ({
                kind: 'result' as const,
                result,
                to:
                    result.entity && result.payload?.id
                        ? `/custom-object/layout/${result.entity}/default/${result.payload.id}/${result.can_update ? 'edit' : 'view'}`
                        : null,
            })),
        ];
    }, [actionTrail.length, actionOptions, actions, results]);

    useEffect(() => setActiveIndex(-1), [navItems]);

    useEffect(() => {
        const fetchOptions = async () => {
            setIsOptionsLoading(true);
            try {
                const res = await http.get(apiEndpoints.globalSearchOption());
                const data = res.data;
                setSearchObjects(Array.isArray(data) ? data : []);
            } catch (error) {
                if (error instanceof AxiosError) {
                    console.error(error.response?.data?.message);
                }
            } finally {
                setIsOptionsLoading(false);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }

        if (actionTrail.length > 0) {
            setIsOptionsFetching(true);
            const delay = setTimeout(
                async () => {
                    try {
                        const res = await http.get(
                            apiEndpoints.globalSearchActionOptions([
                                actionTrail[0].id,
                                actionTrail.slice(1).map((step) => step.id),
                                stepQuery.trim(),
                            ]),
                        );
                        setActionOptions(Array.isArray(res.data?.data) ? res.data.data : []);
                        if (res.data?.csrf) setCsrf(res.data.csrf);
                    } catch (error) {
                        if (error instanceof AxiosError) {
                            console.error(error.response?.data?.message);
                        }
                    } finally {
                        setIsOptionsFetching(false);
                    }
                },
                stepQuery.trim() ? 300 : 0,
            );
            return () => clearTimeout(delay);
        }

        if (query.trim().length < 2) {
            setResults([]);
            setActions([]);
            setIsSearchLoading(false);
            return;
        }

        setIsSearchLoading(true);
        const delay = setTimeout(async () => {
            try {
                const res = await http.get(apiEndpoints.globalSearchPlainResult([selected, query.trim()]));
                setResults(Array.isArray(res.data?.data) ? res.data.data : []);
                setActions(Array.isArray(res.data?.actions) ? res.data.actions : []);
                if (res.data?.csrf) setCsrf(res.data.csrf);
            } catch (error) {
                if (error instanceof AxiosError) {
                    console.error(error.response?.data?.message);
                }
            } finally {
                setIsSearchLoading(false);
            }
        }, 500);
        return () => clearTimeout(delay);
    }, [query, stepQuery, selected, actionTrail]);

    const runAction = (action: QuickAction) => {
        if (!action.url) return;
        if (action.confirm && !window.confirm(action.confirm)) return;

        if (action.method === 'POST') {
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = action.url;
            const token = document.createElement('input');
            token.type = 'hidden';
            token.name = '_token';
            token.value = csrf;
            form.appendChild(token);
            document.body.appendChild(form);
            form.submit();
            return;
        }

        window.location.href = action.url;
    };

    const enterStep = (entry: QuickAction) => {
        setActionTrail((trail) => [
            ...trail,
            {
                id: entry.id,
                label: entry.label,
                parentStepQuery: stepQuery,
                parentOptions: actionOptions,
            },
        ]);
        setActionOptions([]);
        setStepQuery('');
        setIsOpen(true);
    };

    const exitStep = () => {
        const leaving = actionTrail[actionTrail.length - 1];
        if (!leaving) return;

        skipNextFetchRef.current = true;
        setActionTrail((trail) => trail.slice(0, -1));
        setStepQuery(leaving.parentStepQuery);
        setActionOptions(leaving.parentOptions);
        setIsSearchLoading(false);
        setIsOptionsFetching(false);
    };

    const handleSearchRedirect = () => {
        if (!query.trim()) return;
        navigate(`/search?query=${encodeURIComponent(query)}&entity=${selected}`);
        setResults([]);
        setQuery('');
    };

    const closeDropdown = () => {
        setIsOpen(false);
        setResults([]);
        setQuery('');
        setStepQuery('');
        setActions([]);
        setActionTrail([]);
        setActionOptions([]);
    };

    const activateItem = (item: NavItem) => {
        if (item.kind === 'action') {
            if (item.action.nested) enterStep(item.action);
            else runAction(item.action);
            return;
        }
        if (item.to) {
            navigate(item.to);
            closeDropdown();
        }
    };

    const handleNavKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (navItems.length === 0) return;
            setIsOpen(true);
            const step = e.key === 'ArrowDown' ? 1 : -1;
            const next = (activeIndex + step + navItems.length) % navItems.length;
            setActiveIndex(next);
            document.getElementById(`gs-item-${next}`)?.scrollIntoView({ block: 'nearest' });
            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && navItems[activeIndex]) {
                activateItem(navItems[activeIndex]);
            } else if (actionTrail.length === 0) {
                handleSearchRedirect();
            }
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            if (actionTrail.length > 0) exitStep();
            else setIsOpen(false);
        }
    };

    const dropdownVisible = isOpen && (actionTrail.length > 0 || query.length >= 2);
    const activeId = activeIndex >= 0 ? `gs-item-${activeIndex}` : undefined;

    const inputA11y = {
        role: 'combobox' as const,
        'aria-expanded': dropdownVisible,
        'aria-controls': 'gs-listbox',
        'aria-autocomplete': 'list' as const,
        'aria-activedescendant': activeId,
    };

    const renderActionItem = (entry: QuickAction, navIndex: number) => (
        <li key={entry.id} role="option" id={`gs-item-${navIndex}`} aria-selected={activeIndex === navIndex}>
            <button
                type="button"
                tabIndex={-1}
                onClick={() => activateItem({ kind: 'action', action: entry })}
                onMouseEnter={() => setActiveIndex(navIndex)}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-gray-700 transition-colors ${
                    activeIndex === navIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
            >
                <span className="truncate">{entry.label}</span>
                {entry.nested && (
                    <span className="text-gray-400" aria-hidden="true">
                        &rarr;
                    </span>
                )}
            </button>
        </li>
    );

    return (
        <div ref={wrapperRef} className="relative w-full max-w-2xl rounded-sm border border-slate-50">
            <div className="flex overflow-hidden rounded-sm border border-gray-200 bg-white">
                <select
                    name="search-entity"
                    aria-label="Search entity"
                    value={selected}
                    onChange={(e) => {
                        setQuery('');
                        setStepQuery('');
                        setSelected(e.target.value);
                        setActions([]);
                        setActionTrail([]);
                        setActionOptions([]);
                    }}
                    className="min-w-40 cursor-pointer border-r border-gray-200 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-700 outline-none hover:bg-gray-50 focus:bg-white"
                >
                    <option value="all">Search All</option>
                    {isOptionsLoading ? (
                        <option disabled>Loading...</option>
                    ) : (
                        searchObjects.map((obj) => (
                            <option key={obj.api_name} value={obj.api_name}>
                                {obj.label || obj.api_name}
                            </option>
                        ))
                    )}
                </select>
                <input
                    id="global-search-input"
                    type="text"
                    aria-label="Global search"
                    placeholder="Search...."
                    value={query}
                    disabled={actionTrail.length > 0}
                    {...inputA11y}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onKeyDown={handleNavKeys}
                    className="flex-1 bg-gray-50 px-4 py-2 text-xs text-gray-900 outline-none placeholder:text-gray-400 focus:bg-white disabled:text-gray-400"
                />
            </div>

            {dropdownVisible && (
                <div className="absolute z-50 mt-2 max-h-96 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                    {actionTrail.length > 0 && (
                        <>
                            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2">
                                <button
                                    type="button"
                                    onClick={exitStep}
                                    aria-label="Back to previous step"
                                    className="text-sm text-gray-400 hover:text-gray-600"
                                >
                                    &larr;
                                </button>
                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                    {actionTrail.map((step) => step.label).join(' / ')}
                                </span>
                            </div>
                            <div className="border-b border-gray-100 px-4 py-2">
                                <input
                                    type="text"
                                    autoFocus
                                    aria-label={`Search within ${actionTrail[actionTrail.length - 1].label}`}
                                    placeholder="Type to search..."
                                    value={stepQuery}
                                    {...inputA11y}
                                    onChange={(e) => setStepQuery(e.target.value)}
                                    onKeyDown={handleNavKeys}
                                    className="w-full rounded-sm border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:bg-white"
                                />
                            </div>
                            {isOptionsFetching ? (
                                <div className="p-4 text-center text-sm text-gray-500" role="status">
                                    Loading...
                                </div>
                            ) : actionOptions.length > 0 ? (
                                <ul id="gs-listbox" role="listbox" className="divide-y divide-gray-100">
                                    {actionOptions.map((entry, index) => renderActionItem(entry, index))}
                                </ul>
                            ) : (
                                <div className="p-4 text-center text-sm text-gray-500" role="status">
                                    {stepQuery.trim().length >= 2 ? 'No matches found' : 'Type to search...'}
                                </div>
                            )}
                        </>
                    )}
                    {actionTrail.length === 0 && (
                        <div id="gs-listbox" role="listbox">
                            {actions.length > 0 && (
                                <div className="border-b border-gray-100">
                                    <div className="px-4 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
                                        Actions
                                    </div>
                                    <ul className="divide-y divide-gray-100" role="presentation">
                                        {actions.map((entry, index) => renderActionItem(entry, index))}
                                    </ul>
                                </div>
                            )}
                            {isSearchLoading && (
                                <div className="p-4 text-center text-sm text-gray-500" role="status">
                                    Searching...
                                </div>
                            )}
                            {!isSearchLoading && query.length >= 3 && results.length === 0 && actions.length === 0 && (
                                <div className="p-4 text-center text-sm text-gray-500" role="status">
                                    No results found
                                </div>
                            )}
                            {!isSearchLoading && results.length > 0 && (
                                <ul className="divide-y divide-gray-100" role="presentation">
                                    {results.map((item, index) => {
                                        const navIndex = actions.length + index;
                                        const label = item.payload?.casenumber ?? item.payload?.name ?? 'No data';
                                        const entityLabel = item.entity
                                            ? (entityLabels[item.entity] ?? item.entity)
                                            : null;
                                        const navItem = navItems[navIndex];
                                        const to = navItem?.kind === 'result' ? navItem.to : null;

                                        return (
                                            <li
                                                key={item.entity_id ?? index}
                                                role="option"
                                                id={`gs-item-${navIndex}`}
                                                aria-selected={activeIndex === navIndex}
                                            >
                                                <button
                                                    type="button"
                                                    tabIndex={-1}
                                                    disabled={!to}
                                                    onClick={() => navItem && activateItem(navItem)}
                                                    onMouseEnter={() => setActiveIndex(navIndex)}
                                                    className={`block w-full px-4 py-2.5 text-left transition-colors ${
                                                        activeIndex === navIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <div className="flex min-w-0 flex-col">
                                                        <span className="truncate text-sm font-semibold text-blue-600">
                                                            {label}
                                                        </span>
                                                        <span className="truncate text-xs text-gray-500">
                                                            {entityLabel}
                                                        </span>
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default GlobalSearch;
